/**
 * Navbar — top navigation with tabs and menu.
 * Replaces window.Navbar.
 */
import { useState, useEffect, useRef, type FC } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './Icon';
import { THEMES, type ThemeId } from '../hooks/useTheme';

interface Props {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onOpenBatchPuzzles: () => void;
  onOpenUpdates?: () => void;
  updatesCount?: number;
  theme: ThemeId;
  onSetTheme: (id: ThemeId) => void;
}

export const Navbar: FC<Props> = ({
  activeTab,
  setActiveTab,
  onOpenBatchPuzzles,
  onOpenUpdates,
  updatesCount,
  theme,
  onSetTheme,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-50">
      <div className="w-full bg-slate-900 border-b border-slate-800 shadow-xl shadow-black/30 relative">
        <div className="max-w-[1920px] mx-auto flex flex-col md:flex-row justify-between items-center px-6 py-3 gap-4 relative z-10 min-h-[60px]">
          {/* Logo */}
          <div className="flex items-center gap-3 shrink-0 md:flex-1">
            <a
              href="https://witelab.ru"
              className="flex items-center gap-3 hover:opacity-80 transition-opacity group"
            >
              <img src="/favicon/favicon.svg" className="w-10 h-10 object-contain group-hover:scale-105 transition-transform duration-300" alt="Mockup Lab" />
              <span className="font-bold text-white text-lg tracking-tight hidden lg:block">
                Mockup Lab
              </span>
            </a>
          </div>

          {/* Navigation Tabs */}
          <div className="flex bg-slate-800/50 p-1 rounded-xl border border-slate-700/50 backdrop-blur-sm">
            <button
              onClick={() => {
                if (activeTab === 'base' || activeTab === 'cloud') setActiveTab('mockups');
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab !== 'base' && activeTab !== 'cloud'
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <Icon name="images" className="w-4 h-4" />
              Мокапы
            </button>
            <button
              onClick={() => setActiveTab('base')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === 'base'
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <Icon name="cloud-upload" className="w-4 h-4" />
              Галерея
            </button>
            <button
              onClick={() => setActiveTab('cloud')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === 'cloud'
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <Icon name="cloud" className="w-4 h-4" />
              Облако
            </button>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 justify-end md:flex-1" ref={menuRef}>
            <div className="relative">
              <button
                onClick={() => setMenuOpen((o) => !o)}
                className={`flex items-center justify-center w-10 h-10 rounded-xl border transition-all shadow-lg shadow-black/20 ${
                  menuOpen
                    ? 'bg-slate-700 border-slate-600 text-white'
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700 hover:border-slate-600'
                }`}
                title="Меню"
              >
                <Icon name="menu" className="w-5 h-5" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-slate-900/95 backdrop-blur-xl border border-slate-700/60 rounded-xl shadow-2xl shadow-black/40 py-1.5 z-50">
                  <button
                    onClick={() => {
                      onOpenBatchPuzzles();
                      setMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-800/80 transition-colors group w-full text-left"
                  >
                    <Icon
                      name="layout-template"
                      className="w-4 h-4 text-blue-400 group-hover:text-blue-300"
                    />
                    Batch Puzzles
                  </button>
                  <a
                    href="https://gemini.witelab.ru/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-800/80 transition-colors group"
                    onClick={() => setMenuOpen(false)}
                  >
                    <Icon
                      name="sparkles"
                      className="w-4 h-4 text-purple-400 group-hover:text-purple-300"
                    />
                    Gemini
                  </a>
                  <Link
                    to="/excel"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-800/80 transition-colors group"
                    onClick={() => setMenuOpen(false)}
                  >
                    <Icon
                      name="table"
                      className="w-4 h-4 text-emerald-400 group-hover:text-emerald-300"
                    />
                    Excel-менеджер
                  </Link>
                  <div className="mx-3 my-1 border-t border-slate-700/50" />
                  {onOpenUpdates && (
                    <button
                      onClick={() => {
                        onOpenUpdates();
                        setMenuOpen(false);
                      }}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-800/80 transition-colors group w-full text-left"
                    >
                      <Icon
                        name="bell"
                        className="w-4 h-4 text-blue-400 group-hover:text-blue-300"
                      />
                      Обновления
                      {!!updatesCount && (
                        <span className="ml-auto text-[10px] bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded-full">
                          {updatesCount}
                        </span>
                      )}
                    </button>
                  )}
                  <div className="mx-3 my-1 border-t border-slate-700/50" />
                  {/* Theme selector */}
                  <div className="px-4 py-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Тема</div>
                    <div className="flex gap-1">
                      {THEMES.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => onSetTheme(t.id)}
                          className={`flex-1 min-w-0 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold transition-all border ${
                            theme === t.id
                              ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300'
                              : 'bg-slate-800/50 border-slate-700/30 text-slate-400 hover:text-slate-200 hover:border-slate-600/40'
                          }`}
                          title={t.label}
                        >
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {t.icon === 'moon' && <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />}
                            {t.icon === 'contrast' && (
                              <>
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 3v18" />
                                <path d="M12 3a9 9 0 0 1 0 18" fill="currentColor" stroke="none" />
                              </>
                            )}
                            {t.icon === 'sun' && (
                              <>
                                <circle cx="12" cy="12" r="4" />
                                <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
                              </>
                            )}
                          </svg>
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
